// components/kursi-play-v2/generate/KpLiveGenerateView.tsx
// =============================================================================
// Generation loading screen for Kursi Play V2 worlds.
//
// TASK 3: Real server-status polling every 2s via kp-get-generation-status.
//         Replaced fake 15s timer with actual job step tracking.
// TASK 5: Accepts level + time_per_session, forwards to kpLiveApi.
// TASK 10: Retry button on failure — re-runs generation with same params.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { router, usePathname } from 'expo-router';
import { theme } from '@/constants/theme';
import { kpLiveApi } from '@/services/kursi-play-v2/kpLiveApiClient';
import { buildRuntimeHomePath, resolveRuntimeBase } from '@/features/unified-learning/runtime/runtimeRoutes';
import { ClickSpark } from '@/components/learn-premium/PremiumPrimitives';

type KpLanguage = 'he' | 'en' | 'ar';
type KpLevel    = 'beginner' | 'intermediate' | 'advanced';
type KpTime     = '5' | '10' | '20';

type Props = {
  raw_content:      string;
  world_name:       string;
  learning_goal:    string;
  language:         KpLanguage;
  user_id:          string;
  guest_session_token?: string;
  level:            KpLevel;
  time_per_session: KpTime;
};

type GenState =
  | { phase: 'running'; stepKey: GenerationStepKey; stepLabel: string }
  | { phase: 'success'; slug: string; quality_score: number }
  | { phase: 'failure'; error: string };

// Maps server current_step → user-facing Hebrew label
const STEP_LABELS: Record<string, string> = {
  pending:     'מכינים את הסביבה לחילוץ התוכן מהחומר שלך',
  parsing:     'מחלצים נושאים מרכזיים ומונחי מפתח',
  generating:  'בונים שיעורים ותרגול ממוקד לפי המטרות שלך',
  structuring: 'מסדרים מסלול למידה, סקירת טעויות והתקדמות',
  saving:      'מסיימים את ההגדרות ומכינים אותך לתחילת למידה',
  complete:    'החוויה מוכנה!',
  failed:      'הבנייה נכשלה',
};

const GENERATION_FLOW = [
  {
    key: 'parsing',
    title: 'פענוח החומר',
    description: 'זיהוי הנושאים החשובים, המושגים והפערים המרכזיים.',
  },
  {
    key: 'generating',
    title: 'בניית שיעורים ותרגול',
    description: 'יצירת שיעורים ברורים ותרגול שמחובר ישירות לתוכן שלך.',
  },
  {
    key: 'structuring',
    title: 'הכנת מנגנון התקדמות',
    description: 'בניית מסלול, מעקב טעויות ולוגיקת חזרה חכמה.',
  },
  {
    key: 'saving',
    title: 'סגירה והשקה',
    description: 'אריזה סופית של סביבת הלמידה והכנה לכניסה למסלול.',
  },
] as const;

type GenerationStepKey = (typeof GENERATION_FLOW)[number]['key'] | 'pending';

const POLL_INTERVAL_MS = 2_000;

const BG       = '#0B0B14';
const SURFACE  = '#16142A';
const SURFACE2 = '#221F3D';
const SURFACE3 = '#2D2B50';
const TEXT     = '#F0EFF8';
const TEXT_MED = '#9896B4';
const PRIMARY  = theme.colors.primary;
const GREEN    = '#10B981';
const RED      = '#EF4444';

export function KpLiveGenerateView({ raw_content, world_name, learning_goal, language, user_id, guest_session_token, level, time_per_session }: Props) {
  const [state, setState]   = useState<GenState>({ phase: 'running', stepKey: 'pending', stepLabel: STEP_LABELS.pending });
  const [holdRedirect, setHoldRedirect] = useState(false);
  const runtimeBase         = resolveRuntimeBase(usePathname());
  const pollTimerRef        = useRef<ReturnType<typeof setInterval> | null>(null);
  const pulseAnim           = useRef(new Animated.Value(0.88)).current;
  const fadeAnim            = useRef(new Animated.Value(0)).current;
  const hasStarted          = useRef(false);
  useEffect(() => {
    Animated.timing(fadeAnim, { toValue: 1, duration: 400, useNativeDriver: true }).start();
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, { toValue: 1.03, duration: 900, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        Animated.timing(pulseAnim, { toValue: 0.95, duration: 900, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulseAnim, fadeAnim]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const startPolling = useCallback((jobId: string, slug: string) => {
    pollTimerRef.current = setInterval(async () => {
      try {
        const status = await kpLiveApi.getGenerationStatus(jobId);

        if (status.status === 'success') {
          stopPolling();
          setState({ phase: 'success', slug: status.slug ?? slug, quality_score: status.quality_score ?? 75 });
          return;
        }

        if (status.status === 'failed') {
          stopPolling();
          setState({ phase: 'failure', error: status.error_message ?? 'הייצור נכשל. נסה שוב.' });
          return;
        }

        // Still running — update step label
        const stepKey = (status.current_step in STEP_LABELS ? status.current_step : 'generating') as GenerationStepKey;
        const label   = STEP_LABELS[stepKey] ?? STEP_LABELS.generating;
        setState((prev) => prev.phase === 'running' ? { phase: 'running', stepKey, stepLabel: label } : prev);

      } catch {
        // Network error during poll — keep showing last step
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  const runGeneration = useCallback(async () => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    setState({ phase: 'running', stepKey: 'pending', stepLabel: STEP_LABELS.pending });

    try {
      const result = await kpLiveApi.generateWorld({
        raw_content,
        world_name,
        learning_goal,
        language,
        user_id,
        level,
        time_per_session,
      });

      if ('running' in result && result.running) {
        // TASK 3: Edge function returned immediately — start polling
        setState({ phase: 'running', stepKey: 'parsing', stepLabel: STEP_LABELS.parsing });
        startPolling(result.job_id, result.slug);
        return;
      }

      // Edge function processed synchronously (fallback / older deploy)
      setState({ phase: 'failure', error: 'ייצור הסתיים ללא תוצאה תקינה' });

    } catch (err: unknown) {
      setState({ phase: 'failure', error: err instanceof Error ? err.message : 'שגיאת רשת' });
    }
  }, [raw_content, world_name, learning_goal, language, user_id, level, time_per_session, startPolling]);

  // TASK 10: Retry — reset state and re-run generation
  const handleRetry = useCallback(() => {
    stopPolling();
    hasStarted.current = false;
    setState({ phase: 'running', stepKey: 'pending', stepLabel: STEP_LABELS.pending });
    void runGeneration();
  }, [runGeneration, stopPolling]);

  useEffect(() => {
    void runGeneration();
    return stopPolling;
  }, [runGeneration, stopPolling]);

  // Navigate to world home after success
  useEffect(() => {
    if (state.phase !== 'success') return;
    if (holdRedirect) return;
    const t = setTimeout(() => router.replace(
      buildRuntimeHomePath(runtimeBase, state.slug, {
        uid: user_id,
        token: guest_session_token,
      }) as any,
    ), 1600);
    return () => clearTimeout(t);
  }, [state, user_id, guest_session_token, runtimeBase, holdRedirect]);

  const currentStepIdx = state.phase === 'running'
    ? Math.max(0, GENERATION_FLOW.findIndex((step) => step.key === state.stepKey))
    : state.phase === 'success' ? GENERATION_FLOW.length : -1;

  const progressRatio = Math.min(1, Math.max(0.12, (currentStepIdx + 1) / (GENERATION_FLOW.length + 1)));

  if (state.phase === 'running') {
    return (
      <Animated.View style={[s.root, { opacity: fadeAnim }]}>
        <View style={s.runningContent}>
          <View style={s.bgGlowTop} />
          <View style={s.bgGlowBottom} />

          <View style={s.generationCard}>
            <Animated.View style={[s.liveBadge, { transform: [{ scale: pulseAnim }] }]}>
              <View style={s.liveDot} />
              <Text style={s.liveBadgeText}>מייצרים עבורך עכשיו סביבת למידה פעילה</Text>
            </Animated.View>

            <View style={s.runningTextGroup}>
              <Text style={s.runningTitle}>המערכת הופכת את התוכן שלך למסלול למידה</Text>
              <Text style={s.runningSubtitle}>
                עוד רגע תקבל שיעורים, תרגול, סקירת טעויות ומסלול התקדמות ברור.
              </Text>
            </View>

            <View style={s.progressCard}>
              <View style={s.progressTopRow}>
                <Text style={s.progressLabel}>התקדמות הבנייה</Text>
                <Text style={s.progressPhaseText}>
                  שלב {Math.min(GENERATION_FLOW.length, currentStepIdx + 1)} מתוך {GENERATION_FLOW.length}
                </Text>
              </View>
              <View style={s.progressTrack}>
                <View style={[s.progressFill, { width: `${progressRatio * 100}%` }]} />
              </View>
              <Text style={s.progressCurrentText}>{state.stepLabel}</Text>
            </View>

            <View style={s.worldNameBadge}>
              <Text style={s.worldNameLabel}>מסלול בתהליך יצירה</Text>
              <Text style={s.worldNameBadgeText} numberOfLines={1}>{world_name}</Text>
            </View>

            <View style={s.buildingPanel}>
              <Text style={s.buildingPanelTitle}>מה המערכת יוצרת כרגע</Text>
              {GENERATION_FLOW.map((step, index) => {
                const status: 'done' | 'active' | 'upcoming' = index < currentStepIdx
                  ? 'done'
                  : index === currentStepIdx
                    ? 'active'
                    : 'upcoming';
                return (
                  <View
                    key={step.key}
                    style={[
                      s.buildingPanelRow,
                      status === 'active' && s.buildingPanelRowActive,
                    ]}
                  >
                    <View
                      style={[
                        s.statusPill,
                        status === 'done' && s.statusPillDone,
                        status === 'active' && s.statusPillActive,
                      ]}
                    >
                      <Text
                        style={[
                          s.statusPillText,
                          status !== 'upcoming' && s.statusPillTextOn,
                        ]}
                      >
                        {status === 'done' ? 'הושלם' : status === 'active' ? 'בתהליך' : 'בהמשך'}
                      </Text>
                    </View>
                    <View style={s.buildingPanelTexts}>
                      <Text style={[s.buildingPanelItemTitle, status !== 'upcoming' && s.buildingPanelItemTitleOn]}>
                        {step.title}
                      </Text>
                      <Text style={s.buildingPanelItemDesc}>{step.description}</Text>
                    </View>
                  </View>
                );
              })}
            </View>

            <Text style={s.runningNote}>
              אפשר לערוך ולדייק את התוצאה גם בהמשך. משך הבנייה תלוי בכמות ובבהירות החומר.
            </Text>
          </View>
        </View>
      </Animated.View>
    );
  }

  if (state.phase === 'success') {
    // Estimate lesson/scenario counts from time_per_session for display
    const lessonCount    = time_per_session === '5' ? 2 : time_per_session === '20' ? 4 : 3;
    const scenarioCount  = time_per_session === '5' ? 4 : time_per_session === '20' ? 16 : 9;

    return (
      <Animated.View style={[s.root, { opacity: fadeAnim }]}>
        <View style={s.stateContent}>
          <View style={[s.stateCard, { borderColor: GREEN + '44' }]}>
            <View style={[s.stateTopLine, { backgroundColor: GREEN }]} />
            <View style={s.stateCardBody}>
              <View style={[s.stateIconWrap, { backgroundColor: GREEN + '18' }]}>
                <Text style={s.stateIconEmoji}>✓</Text>
              </View>
              <Text style={s.stateTitle}>חוויית הלמידה מוכנה</Text>
              <Text style={[s.stateHint, { color: TEXT_MED, marginTop: -6 }]}>
                נבנה עבורך:
              </Text>

              {/* What was built — the actual value, not a score */}
              <View style={s.successOutputGrid}>
                <View style={s.successOutputItem}>
                  <Text style={s.successOutputNum}>{lessonCount}</Text>
                  <Text style={s.successOutputLabel}>שיעורים</Text>
                </View>
                <View style={s.successOutputDivider} />
                <View style={s.successOutputItem}>
                  <Text style={s.successOutputNum}>{scenarioCount}</Text>
                  <Text style={s.successOutputLabel}>תרחישים</Text>
                </View>
                <View style={s.successOutputDivider} />
                <View style={s.successOutputItem}>
                  <Text style={s.successOutputNum}>✓</Text>
                  <Text style={s.successOutputLabel}>מדריך AI</Text>
                </View>
              </View>

              <Text style={[s.stateHint, { color: TEXT_MED }]}>
                {holdRedirect ? 'המעבר האוטומטי נעצר. אפשר לבחור את הצעד הבא.' : 'מעביר אותך אוטומטית למסלול בעוד רגע...'}
              </Text>
              <View style={s.successActions}>
                <Pressable style={s.successPrimaryBtn} onPress={() => router.replace(buildRuntimeHomePath(runtimeBase, state.slug, { uid: user_id }) as any)}>
                  <Text style={s.successPrimaryBtnText}>היכנס למסלול עכשיו</Text>
                </Pressable>
                <Pressable style={s.successSecondaryBtn} onPress={() => {
                  setHoldRedirect(true);
                  router.replace('/learn/creator' as any);
                }}>
                  <Text style={s.successSecondaryBtnText}>סקירה לפני כניסה</Text>
                </Pressable>
              </View>
            </View>
          </View>
        </View>
      </Animated.View>
    );
  }

  // Failure state
  const isQualityFail  = state.phase === 'failure' && state.error.includes('ציון האיכות');
  const isContentShort = state.phase === 'failure' && state.error.includes('קצר מדי');

  return (
    <Animated.View style={[s.root, { opacity: fadeAnim }]}>
      <View style={s.stateContent}>
        <View style={[s.stateCard, { borderColor: RED + '44' }]}>
          <View style={[s.stateTopLine, { backgroundColor: RED }]} />
          <View style={s.stateCardBody}>
            <View style={[s.stateIconWrap, { backgroundColor: RED + '18' }]}>
              <Text style={s.stateIconEmoji}>⚠</Text>
            </View>
            <Text style={[s.stateTitle, { color: RED }]}>הבנייה לא הסתיימה</Text>

            {/* Human, actionable error — not a raw error dump */}
            {isQualityFail ? (
              <>
                <Text style={s.stateBodyText}>
                  החומר שסיפקת לא הספיק ליצירת חוויה מלאה.
                </Text>
                <Text style={s.stateBodyHint}>
                  נסה להוסיף יותר תוכן ממוקד — לפחות 3-4 פסקאות על הנושא.
                </Text>
              </>
            ) : isContentShort ? (
              <>
                <Text style={s.stateBodyText}>החומר קצר מדי לבניית חוויית למידה.</Text>
                <Text style={s.stateBodyHint}>הוסף לפחות 50 תווים של תוכן.</Text>
              </>
            ) : (
              <>
                <Text style={s.stateBodyText}>
                  {state.phase === 'failure' ? state.error : 'שגיאה לא צפויה'}
                </Text>
                <Text style={s.stateBodyHint}>
                  אם הבעיה חוזרת, נסה לשנות את התוכן או לרענן את הדף.
                </Text>
              </>
            )}

            <View style={s.stateActions}>
              <ClickSpark onPress={handleRetry}>
                <View style={s.primaryBtn}>
                  <Text style={s.primaryBtnText}>נסה שוב</Text>
                </View>
              </ClickSpark>
              <Pressable
                style={s.secondaryBtn}
                onPress={() => router.replace('/learn/path/create' as any)}
                accessibilityRole="button"
              >
                <Text style={s.secondaryBtnText}>שנה תוכן</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </View>
    </Animated.View>
  );
}

const s = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: BG,
  },

  runningContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    paddingVertical: 24,
  },
  bgGlowTop: {
    position: 'absolute',
    top: -120,
    width: 420,
    height: 320,
    borderRadius: 220,
    backgroundColor: '#4C1D9538',
    ...(Platform.OS === 'web' ? ({ filter: 'blur(42px)' } as any) : {}),
  },
  bgGlowBottom: {
    position: 'absolute',
    bottom: -140,
    width: 360,
    height: 280,
    borderRadius: 200,
    backgroundColor: '#1E3A8A26',
    ...(Platform.OS === 'web' ? ({ filter: 'blur(42px)' } as any) : {}),
  },
  generationCard: {
    width: '100%',
    maxWidth: 740,
    borderRadius: 28,
    borderWidth: 1,
    borderColor: '#4D486C',
    backgroundColor: '#111123E8',
    padding: 22,
    gap: 18,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 28px 70px rgba(2,4,15,0.7)' } as any) : {}),
  },
  liveBadge: {
    alignSelf: 'center',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#605A88',
    backgroundColor: '#1A1733',
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: PRIMARY,
  },
  liveBadgeText: {
    fontFamily: theme.fonts.medium,
    fontSize: 12,
    color: '#C8C3EB',
  },

  runningTextGroup: { alignItems: 'center', gap: 10 },
  runningTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 32,
    color: TEXT,
    textAlign: 'center',
    lineHeight: 42,
    maxWidth: 620,
  },
  runningSubtitle: {
    fontFamily: theme.fonts.regular,
    fontSize: 16,
    color: '#C0BEDA',
    textAlign: 'center',
    lineHeight: 24,
    maxWidth: 600,
  },

  progressCard: {
    gap: 10,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#4A456B',
    backgroundColor: '#17162D',
    padding: 14,
  },
  progressTopRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  progressLabel: {
    fontFamily: theme.fonts.medium,
    fontSize: 13,
    color: '#E4E1F8',
  },
  progressPhaseText: {
    fontFamily: theme.fonts.regular,
    fontSize: 12,
    color: '#A5A0C5',
  },
  progressTrack: {
    height: 9,
    borderRadius: 999,
    overflow: 'hidden',
    backgroundColor: '#2A2844',
  },
  progressFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: PRIMARY,
  },
  progressCurrentText: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: '#C9C5E4',
    textAlign: 'right',
  },

  worldNameBadge: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#444060',
    backgroundColor: '#161428',
    paddingHorizontal: 14,
    paddingVertical: 11,
    alignItems: 'flex-end',
    gap: 4,
  },
  worldNameLabel: {
    fontFamily: theme.fonts.regular,
    fontSize: 11,
    color: '#9A96B9',
  },
  worldNameBadgeText: {
    fontFamily: theme.fonts.medium,
    fontSize: 15,
    color: '#ECEAFE',
    textAlign: 'right',
  },

  stateContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  stateCard: {
    backgroundColor: SURFACE,
    borderRadius: 24,
    borderWidth: 1,
    overflow: 'hidden',
    width: '100%',
    maxWidth: 400,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 24px 60px rgba(0,0,0,0.6)' } as any) : {}),
  },
  stateTopLine: { height: 3, width: '100%' },
  stateCardBody: {
    padding: 28,
    alignItems: 'center',
    gap: 14,
  },

  stateIconWrap: {
    width: 72,
    height: 72,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stateIconEmoji: { fontSize: 34 },

  stateTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 24,
    color: GREEN,
    textAlign: 'center',
    lineHeight: 32,
  },
  stateSubtitle: {
    fontFamily: theme.fonts.medium,
    fontSize: 15,
    color: TEXT_MED,
    textAlign: 'center',
    marginTop: -4,
  },
  stateHint: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    textAlign: 'center',
  },
  stateBodyText: {
    fontFamily: theme.fonts.regular,
    fontSize: 14,
    color: TEXT_MED,
    textAlign: 'center',
    lineHeight: 21,
    maxWidth: 300,
  },

  stateActions: {
    width: '100%',
    gap: 10,
    marginTop: 6,
    borderTopWidth: 1,
    borderTopColor: SURFACE2,
    paddingTop: 16,
  },
  primaryBtn: {
    backgroundColor: PRIMARY,
    borderRadius: 14,
    paddingVertical: 15,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? ({ boxShadow: `0 6px 20px ${PRIMARY}55` } as any) : {}),
  },
  primaryBtnText: {
    fontFamily: theme.fonts.bold,
    fontSize: 15,
    color: '#fff',
  },
  secondaryBtn: {
    backgroundColor: SURFACE2,
    borderRadius: 14,
    paddingVertical: 13,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: SURFACE3,
  },
  secondaryBtnText: {
    fontFamily: theme.fonts.medium,
    fontSize: 14,
    color: TEXT_MED,
  },

  buildingPanel: {
    backgroundColor: '#161428',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#444060',
    padding: 14,
    gap: 12,
  },
  buildingPanelTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 16,
    color: '#F2F0FF',
    textAlign: 'right',
  },
  buildingPanelRow: {
    flexDirection: 'row-reverse',
    gap: 10,
    borderRadius: 12,
    padding: 8,
  },
  buildingPanelRowActive: {
    backgroundColor: '#211B3D',
    borderWidth: 1,
    borderColor: '#5B4A8D',
  },
  statusPill: {
    alignSelf: 'flex-start',
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#514C71',
    paddingVertical: 5,
    paddingHorizontal: 8,
    minWidth: 58,
    alignItems: 'center',
    backgroundColor: '#1A1730',
  },
  statusPillDone: {
    backgroundColor: '#063F2C',
    borderColor: '#0E8761',
  },
  statusPillActive: {
    backgroundColor: '#35226A',
    borderColor: '#7B61D9',
  },
  statusPillText: {
    fontFamily: theme.fonts.medium,
    fontSize: 10,
    color: '#9F9ABF',
  },
  statusPillTextOn: {
    color: '#EDF9F4',
  },
  buildingPanelTexts: {
    flex: 1,
    gap: 4,
  },
  buildingPanelItemTitle: {
    fontFamily: theme.fonts.medium,
    fontSize: 14,
    color: '#A9A4CB',
    textAlign: 'right',
  },
  buildingPanelItemTitleOn: {
    color: '#F1EEFF',
  },
  buildingPanelItemDesc: {
    fontFamily: theme.fonts.regular,
    fontSize: 12,
    color: '#A49EC6',
    textAlign: 'right',
    lineHeight: 18,
  },
  runningNote: {
    fontFamily: theme.fonts.regular,
    fontSize: 12,
    color: '#9691B7',
    textAlign: 'center',
    lineHeight: 18,
  },

  // Success output grid
  successOutputGrid: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0,
    backgroundColor: SURFACE2,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: SURFACE3,
    padding: 12,
    width: '100%',
  },
  successOutputItem: {
    flex: 1,
    alignItems: 'center',
    gap: 4,
  },
  successOutputNum: {
    fontFamily: theme.fonts.bold,
    fontSize: 22,
    color: GREEN,
  },
  successOutputLabel: {
    fontFamily: theme.fonts.regular,
    fontSize: 12,
    color: TEXT_MED,
  },
  successActions: {
    width: '100%',
    gap: 8,
    marginTop: 6,
  },
  successPrimaryBtn: {
    borderRadius: 10,
    backgroundColor: PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 42,
  },
  successPrimaryBtnText: { color: '#fff', fontFamily: theme.fonts.bold, fontSize: 14 },
  successSecondaryBtn: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: SURFACE3,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
    backgroundColor: SURFACE2,
  },
  successSecondaryBtnText: { color: TEXT, fontFamily: theme.fonts.medium, fontSize: 13 },
  successOutputDivider: {
    width: 1,
    height: 36,
    backgroundColor: SURFACE3,
  },

  // Failure state body text
  stateBodyHint: {
    fontFamily: theme.fonts.regular,
    fontSize: 12,
    color: TEXT_MED,
    textAlign: 'center',
    lineHeight: 18,
    maxWidth: 280,
  },
});
